export type LogLevel = 'info' | 'warn' | 'error' | 'success';

export interface ConnectionLogEntry {
  id: string;
  timestamp: string;       // ISO
  level: LogLevel;
  source: string;          // e.g. "SensorPoll", "TelegramBot", "HABridge"
  message: string;
  detail?: string;         // stack trace, raw response, etc.
}

const MAX_ENTRIES = 50;

export class ConnectionLog {
  private entries: ConnectionLogEntry[] = [];
  private listeners: (() => void)[] = [];

  subscribe(fn: () => void) {
    this.listeners.push(fn);
    return () => { this.listeners = this.listeners.filter(l => l !== fn); };
  }

  private notify() {
    this.listeners.forEach(fn => fn());
  }

  push(entry: Omit<ConnectionLogEntry, 'id' | 'timestamp'>) {
    const full: ConnectionLogEntry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      timestamp: new Date().toISOString(),
      ...entry,
    };
    this.entries = [full, ...this.entries].slice(0, MAX_ENTRIES);
    this.notify();
    return full;
  }

  info(source: string, message: string, detail?: string) {
    return this.push({ level: 'info', source, message, detail });
  }
  warn(source: string, message: string, detail?: string) {
    return this.push({ level: 'warn', source, message, detail });
  }
  error(source: string, message: string, detail?: string) {
    return this.push({ level: 'error', source, message, detail });
  }
  success(source: string, message: string, detail?: string) {
    return this.push({ level: 'success', source, message, detail });
  }

  getAll(): ConnectionLogEntry[] {
    return this.entries;
  }

  clear() {
    this.entries = [];
    this.notify();
  }
}

// Singleton shared across the app
export const connectionLog = new ConnectionLog();
